// Locale formatting for ar-IQ (HANDOFF-erp-web §2, business handoff §2,
// locales.json). Display only — these helpers never add amounts together
// (rule 7: every figure comes from an API response).
//
//   money     ١٬٢٦٠٬٠٠٠ د.ع          (IQD, 0 decimals, Arabic-Indic, ٬ grouping)
//   USD hint  ≈ ١٤٠ $ بسعر ١٬٣٢٠     (at the document's frozen fx_rate)
//   dates     الثلاثاء ١٥ أيلول ٢٠٢٦  (Asia/Baghdad, Iraqi month names)
//   week      starts Saturday (IQ profile)

import locales from './locales.json';
import { toArabicDigits } from './digits';

export { toArabicDigits } from './digits';

export const BAGHDAD_TZ = 'Asia/Baghdad';

interface CountryProfile {
  currency: string;
  symbol_ar: string;
  decimals: number;
  group: string;
  week_start: string;
  months: string;
  digits_ar: string;
}

const COUNTRIES = locales.countries as Record<string, CountryProfile>;
const MONTH_SETS = locales.month_sets as Record<string, string[]>;
export const IQ: CountryProfile = COUNTRIES.IQ;

/** Iraqi month names (كانون الثاني … كانون الأول). */
export const IRAQI_MONTHS: readonly string[] = MONTH_SETS[IQ.months];

/** Weekday names, index 0 = Sunday (JS getDay order). */
export const AR_WEEKDAYS: readonly string[] = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

/** Week order for IQ: Saturday first. */
export const IQ_WEEK_ORDER: readonly number[] = [6, 0, 1, 2, 3, 4, 5];

const CURRENCY_DECIMALS: Record<string, number> = { IQD: 0, USD: 2 };
const CURRENCY_SYMBOL_AR: Record<string, string> = { IQD: 'د.ع', USD: '$' };

/** Group an integer string's thousands with `sep`. */
function group(intDigits: string, sep: string): string {
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

/** Integer with Arabic thousands separator + Arabic-Indic digits: 1260000 → ١٬٢٦٠٬٠٠٠. */
export function arNumber(n: number, opts: { decimals?: number } = {}): string {
  if (!Number.isFinite(n)) return '';
  const decimals = opts.decimals ?? 0;
  const neg = n < 0;
  const fixed = Math.abs(n).toFixed(decimals);
  const [int, frac] = fixed.split('.');
  const body = group(int, IQ.group) + (frac ? `٫${frac}` : '');
  return (neg ? '−' : '') + toArabicDigits(body);
}

/**
 * Money from minor units. IQD minor = dinars (0 decimals) → '١٬٢٦٠٬٠٠٠ د.ع'.
 * `unit:false` returns the bare number (for table cells whose column carries the unit).
 * `signed:true` prefixes '+' for positives ('−' is always shown for negatives).
 */
export function formatMoney(
  minor: number,
  currency = 'IQD',
  opts: { unit?: boolean; signed?: boolean } = {},
): string {
  const decimals = CURRENCY_DECIMALS[currency] ?? 2;
  const value = minor / 10 ** decimals;
  let s = arNumber(value, { decimals: currency === 'IQD' ? 0 : decimals });
  if (opts.signed && minor > 0) s = `+${s}`;
  if (opts.unit === false) return s;
  const sym = CURRENCY_SYMBOL_AR[currency] ?? currency;
  return `${s} ${sym}`;
}

/** Shorthand: IQD amount in dinars → '١٢٠٬٠٠٠ د.ع'. */
export function iqd(dinars: number, opts: { unit?: boolean; signed?: boolean } = {}): string {
  return formatMoney(Math.round(dinars), 'IQD', opts);
}

/** fx rate (IQD per 1 USD) as Arabic digits: 1320 → '١٬٣٢٠'. */
export function formatRate(fxRate: number): string {
  return arNumber(fxRate, { decimals: Number.isInteger(fxRate) ? 0 : 2 });
}

/** USD cents (a SERVER figure, e.g. balance_usd_cents) → '١١٫٣٦ $' ('١٤٠ $' when whole). */
export function formatUsdCents(cents: number): string {
  return `${arNumber(cents / 100, { decimals: cents % 100 === 0 ? 0 : 2 })} $`;
}

/**
 * The amount field's rate hint for a SERVER-computed equivalent (rule 4 /
 * accounts-documents spec: the client never converts USD):
 *   USD equivalent 1136 cents at 1320 → '≈ ١١٫٣٦ $ بسعر ١٬٣٢٠'
 *   IQD equivalent 244200 at 1320     → '≈ ٢٤٤٬٢٠٠ د.ع بسعر ١٬٣٢٠'
 */
export function fxEquivalentHint(equivalentMinor: number, equivalentCurrency: 'IQD' | 'USD', fxRate: number): string {
  const amount = equivalentCurrency === 'USD' ? formatUsdCents(equivalentMinor) : formatMoney(equivalentMinor, 'IQD');
  return `≈ ${amount} بسعر ${formatRate(fxRate)}`;
}

/**
 * @deprecated Converts on the client (rule 4 forbids it) and rounds to whole
 * dollars. Use a server figure with fxEquivalentHint / AmountField `quote`.
 * Kept only until the vouchers panel migrates (SHARED-REQUESTS 2026-10-12).
 */
export function usdHint(iqdMinor: number, fxRate: number): string {
  if (!fxRate || fxRate <= 0) return '';
  return `≈ ${arNumber(Math.round(iqdMinor / fxRate))} $ بسعر ${formatRate(fxRate)}`;
}

/** USD amount (whole dollars) → '٩٥٥ $'. */
export function usd(dollars: number): string {
  return `${arNumber(Math.round(dollars))} $`;
}

/**
 * @deprecated Converts on the client (rule 4 forbids it). Kept only until the
 * vouchers panel migrates (SHARED-REQUESTS 2026-10-12).
 */
export function iqdEquivalentOfUsd(usdCents: number, fxRate: number): number {
  return Math.round((usdCents / 100) * fxRate);
}

/** 9.4 → '٩٫٤٪' ; signed: +3 → '+٣٪'. */
export function arPercent(n: number, opts: { signed?: boolean } = {}): string {
  const s = toArabicDigits(Math.abs(n).toString().replace('.', '٫'));
  const sign = n < 0 ? '−' : opts.signed && n > 0 ? '+' : '';
  return `${sign}${s}٪`;
}

/** Arabic count agreement for local queue documents (board copy «٣ مستندات محلية»). */
export function localDocsLabel(n: number): string {
  if (n === 1) return 'مستند محلي واحد';
  if (n === 2) return 'مستندان محليان';
  if (n <= 10) return `${arNumber(n)} مستندات محلية`;
  return `${arNumber(n)} مستنداً محلياً`;
}

// ---------------------------------------------------------------- dates --

export interface BaghdadParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  weekday: number; // 0 = Sunday
}

const partsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: BAGHDAD_TZ,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  weekday: 'short',
  hourCycle: 'h23',
});
const WEEKDAY_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDate(d: Date | string | number): Date {
  return d instanceof Date ? d : new Date(d);
}

/** Wall-clock parts of an instant in Baghdad time. */
export function baghdadParts(d: Date | string | number): BaghdadParts {
  const out: Record<string, string> = {};
  for (const p of partsFmt.formatToParts(toDate(d))) out[p.type] = p.value;
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
    weekday: WEEKDAY_EN.indexOf(out.weekday),
  };
}

/** Calendar date (YYYY-MM-DD) is a plain date — no timezone shift. */
function isPlainDate(d: unknown): d is string {
  return typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d);
}

function plainParts(d: string): BaghdadParts {
  const [y, m, day] = d.split('-').map(Number);
  const weekday = new Date(Date.UTC(y, m - 1, day)).getUTCDay();
  return { year: y, month: m, day, hour: 0, minute: 0, weekday };
}

function partsOf(d: Date | string | number): BaghdadParts {
  return isPlainDate(d) ? plainParts(d) : baghdadParts(d);
}

/** '١٥ أيلول' ; withYear → '١٥ أيلول ٢٠٢٦' ; withWeekday → 'الثلاثاء ١٥ أيلول ٢٠٢٦'. */
export function formatDate(
  d: Date | string | number,
  opts: { year?: boolean; weekday?: boolean } = {},
): string {
  const p = partsOf(d);
  let s = `${toArabicDigits(p.day)} ${IRAQI_MONTHS[p.month - 1]}`;
  if (opts.year) s += ` ${toArabicDigits(p.year)}`;
  if (opts.weekday) s = `${AR_WEEKDAYS[p.weekday]} ${s}`;
  return s;
}

/** 12h time with ص/م: '١١:٢٤ ص'. `suffix:false` → '١١:٢٤'. */
export function formatTime(d: Date | string | number, opts: { suffix?: boolean } = {}): string {
  const p = baghdadParts(d);
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const s = `${toArabicDigits(h12)}:${toArabicDigits(String(p.minute).padStart(2, '0'))}`;
  if (opts.suffix === false) return s;
  return `${s} ${p.hour < 12 ? 'ص' : 'م'}`;
}

/** 'الثلاثاء ١٥ أيلول · ١١:٢٤ ص' — the DocHead / panel timestamp. */
export function formatDateTime(d: Date | string | number): string {
  return `${formatDate(d, { weekday: true })} · ${formatTime(d)}`;
}

/** Period 'YYYY-MM' → 'أيلول ٢٠٢٦' (yearless: 'أيلول'). */
export function formatPeriod(period: string, opts: { year?: boolean } = { year: true }): string {
  const [y, m] = period.split('-').map(Number);
  const name = IRAQI_MONTHS[m - 1];
  return opts.year === false ? name : `${name} ${toArabicDigits(y)}`;
}

/** Month name for 1-12. */
export function monthName(month: number): string {
  return IRAQI_MONTHS[month - 1];
}

/** Today's calendar date in Baghdad as YYYY-MM-DD. */
export function baghdadToday(now: Date = new Date()): string {
  const p = baghdadParts(now);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Current period YYYY-MM in Baghdad. */
export function baghdadPeriod(now: Date = new Date()): string {
  return baghdadToday(now).slice(0, 7);
}

function addDaysPlain(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** A plain date ± n days ('2026-10-15', 3 → '2026-10-18'); no timezone shift. */
export const addDays = addDaysPlain;

/** Whole nights between two plain dates: nightsBetween('2026-10-11', '2026-10-15') = 4. */
export function nightsBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The Saturday that starts the IQ week containing `date` (YYYY-MM-DD). */
export function weekStart(date: string): string {
  const wd = plainParts(date).weekday; // 0 Sun … 6 Sat
  const back = (wd + 1) % 7; // Sat → 0, Sun → 1, … Fri → 6
  return addDaysPlain(date, -back);
}

/** The seven dates (YYYY-MM-DD) of the IQ week containing `date`, Saturday first. */
export function weekDays(date: string): string[] {
  const start = weekStart(date);
  return Array.from({ length: 7 }, (_, i) => addDaysPlain(start, i));
}

/** True when the date falls on the IQ weekend (Friday). */
export function isWeekend(date: string): boolean {
  return plainParts(date).weekday === 5;
}
